import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Layout, Card, Typography, Row, Col, Tag, Space, Avatar, Spin, Alert, List, Select,
  Modal, Form, Button, message
} from 'antd';
import {
  CalendarOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  BookOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  EditOutlined
} from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const { Content } = Layout;
const { Title, Text } = Typography;

const StudentTimeline = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [registrations, setRegistrations] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });

  // Edit Modal States
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Dropdown data
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL;

  // Time options 08:00–23:00 every 15 mins
  const timeOptions = Array.from({ length: (23 - 8 + 1) * 4 }, (_, index) => {
    const hour = 8 + Math.floor(index / 4);
    const minutes = (index % 4) * 15;
    const label = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    return { label, value: label + ":00" };
  });

  // Hour options 0:15 → 3:00 (in minutes)
  const hourOptions = Array.from({ length: 12 }, (_, index) => {
    const minutes = (index + 1) * 15; // 15, 30, …, 180
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return {
      label: `${h}h ${m.toString().padStart(2, "0")}m`,
      value: minutes
    };
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        const [registrationsRes, attendanceRes, teachersRes, subjectsRes] = await Promise.all([
          axios.get(`${API_URL}/registrations/student/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/attendance/student/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/teachers`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/subjects`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setRegistrations(registrationsRes.data.registrations);
        setAttendance(attendanceRes.data);
        setTeachers(teachersRes.data.teachers?.filter(t => t.status === 'active') || []);
        setSubjects(subjectsRes.data || []);
      } catch (error) {
        console.error('❌ Error fetching student data:', error);
        setError(error.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, API_URL]);

  const getAttendanceForClass = (classId) => {
    return attendance.find((log) => log.classid === classId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours}:${minutes}`;
  };

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '';
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    
    let durationHours = 0;
    let durationMinutes = 0;
    
    if (duration.includes(':')) {
      [durationHours, durationMinutes] = duration.split(':').map(Number);
    } else {
      durationHours = parseFloat(duration);
      durationMinutes = (durationHours % 1) * 60;
      durationHours = Math.floor(durationHours);
    }

    let endHours = startHours + durationHours;
    let endMinutes = startMinutes + durationMinutes;
    
    if (endMinutes >= 60) {
      endHours += Math.floor(endMinutes / 60);
      endMinutes %= 60;
    }
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const getStatusConfig = (status) => {
    const configs = {
      'Present': {
        color: 'success',
        text: 'Attendance Checked by Staff : Present',
        icon: CheckCircleOutlined
      },
      'Late': {
        color: 'warning',
        text: t('late') || 'Late', 
        icon: ExclamationCircleOutlined
      },
      'Absent': {
        color: 'error',
        text: t('absent') || 'Absent',
        icon: CloseCircleOutlined
      },
      'To be rescheduled': {
        color: 'purple',
        text: t('toBeRescheduled') || 'To be rescheduled',
        icon: ClockCircleOutlined
      },
      'No Attendance': {
        color: 'default',
        text: t('scheduled') || 'Scheduled',
        icon: ClockCircleOutlined
      }
    };
    return configs[status] || configs['No Attendance'];
  };

  // Open Edit Modal
  const handleEditClick = (cls) => {
    console.log('📝 Editing class:', cls);
    setEditingClass(cls);
    setEditModalVisible(true);
    
    // Parse schedule_hour to minutes
    let scheduleMinutes = 60; // default
    if (cls.hours) {
      if (cls.hours.includes('h') && cls.hours.includes('m')) {
        const hourMatch = cls.hours.match(/(\d+)h/);
        const minuteMatch = cls.hours.match(/(\d+)m/);
        scheduleMinutes = (hourMatch ? parseInt(hourMatch[1]) * 60 : 0) + 
                         (minuteMatch ? parseInt(minuteMatch[1]) : 0);
      }
    }

    // Format date for HTML input (YYYY-MM-DD)
    const dateValue = cls.date ? moment(cls.date).format('YYYY-MM-DD') : '';
    console.log('📅 Setting date in form:', cls.date, '→', dateValue);

    editForm.setFieldsValue({
      date: dateValue,
      schedule_time: cls.time || null,
      schedule_hour: scheduleMinutes,
      teacher_id: cls.teacher_id || null,
      subject_id: cls.subject_id || null,
    });
  };

  // Save Edited Class
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      console.log('💾 Form values:', values);
      
      setSaving(true);

      const token = localStorage.getItem('token');

      const payload = {
        date: values.date, // Already in YYYY-MM-DD format from HTML input
        schedule_time: values.schedule_time,
        schedule_hour: values.schedule_hour,
        teacher_id: values.teacher_id,
        subjectid: values.subject_id,
      };

      console.log('📤 Sending payload:', payload);

      await axios.put(
        `${API_URL}/classes/${editingClass.classid}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success(t('Class updated successfully'));
      setEditModalVisible(false);
      editForm.resetFields();
      
      // Refresh data
      const [registrationsRes, attendanceRes] = await Promise.all([
        axios.get(`${API_URL}/registrations/student/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/attendance/student/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setRegistrations(registrationsRes.data.registrations);
      setAttendance(attendanceRes.data);

    } catch (error) {
      console.error('❌ Error updating class:', error);
      message.error(t('Failed to update class'));
    } finally {
      setSaving(false);
    }
  };

  // Get all timeline items with date range filter
  const getTimelineItems = () => {
    let items = registrations
      .flatMap(reg => 
        (reg.schedule || []).map(cls => ({
          ...cls,
          class_code: reg.class_code,
          attendanceLog: getAttendanceForClass(cls.classid)
        }))
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    // Apply status filter
    if (statusFilter !== 'all') {
      items = items.filter(cls => {
        const status = cls.attendanceLog ? cls.attendanceLog.status : 'No Attendance';
        return status === statusFilter;
      });
    }

    // Apply date range filter (using native date strings)
    if (dateRange.startDate && dateRange.endDate) {
      items = items.filter(cls => {
        const classDate = cls.date; // Format: YYYY-MM-DD
        return classDate >= dateRange.startDate && classDate <= dateRange.endDate;
      });
    }

    return items;
  };

  const timelineItems = getTimelineItems();

  const getStats = () => {
    const total = timelineItems.length;
    const present = timelineItems.filter(cls => cls.attendanceLog?.status === 'Present').length;
    const late = timelineItems.filter(cls => cls.attendanceLog?.status === 'Late').length;
    const absent = timelineItems.filter(cls => cls.attendanceLog?.status === 'Absent').length;
    const toBeRescheduled = timelineItems.filter(cls => cls.attendanceLog?.status === 'To be rescheduled').length;
    const noAttendance = timelineItems.filter(cls => !cls.attendanceLog).length;

    return { total, present, late, absent, toBeRescheduled, noAttendance };
  };

  const stats = getStats();

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Layout>
          <Content style={{ padding: '50px', textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 20 }}>
              <Text>{t('Loading timeline data...')}</Text>
            </div>
          </Content>
        </Layout>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Layout>
          <Content style={{ padding: '50px' }}>
            <Alert
              message="Error Loading Data"
              description={error}
              type="error"
              showIcon
              style={{ maxWidth: 600, margin: '0 auto' }}
            />
          </Content>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout>
        <Content style={{ padding: '24px', minHeight: 280 }}>
          {/* Header with Title and Filters */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={12}>
                <Title level={3} style={{ margin: 0 }}>
                  <CalendarOutlined /> {t('Activity Timeline') || 'Activity Timeline'}
                </Title>
              </Col>
              <Col xs={24} md={12}>
                <Space wrap style={{ float: 'right' }}>
                  <Space>
                    <input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) =>
                        setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
                      }
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) =>
                        setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
                      }
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    />
                  </Space>

                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 150 }}
                    suffixIcon={<FilterOutlined />}
                  >
                    <Select.Option value="all">{t('All Status')}</Select.Option>
                    <Select.Option value="Present">{t('Present')}</Select.Option>
                    <Select.Option value="Late">{t('Late')}</Select.Option>
                    <Select.Option value="Absent">{t('Absent')}</Select.Option>
                    <Select.Option value="To be rescheduled">{t('toBeRescheduled')}</Select.Option>
                    <Select.Option value="No Attendance">{t('Scheduled')}</Select.Option>
                  </Select>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Statistics Summary */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f0f9ff', border: '1px solid #bae7ff' }}>
                <Text strong style={{ fontSize: '24px', color: '#1890ff' }}>{stats.total}</Text>
                <br />
                <Text type="secondary">Total Classes</Text>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                <Text strong style={{ fontSize: '24px', color: '#52c41a' }}>{stats.present}</Text>
                <br />
                <Text type="secondary">Present</Text>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center', backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
                <Text strong style={{ fontSize: '24px', color: '#faad14' }}>{stats.late}</Text>
                <br />
                <Text type="secondary">Late</Text>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center', backgroundColor: '#fff2f0', border: '1px solid #ffccc7' }}>
                <Text strong style={{ fontSize: '24px', color: '#ff4d4f' }}>{stats.absent}</Text>
                <br />
                <Text type="secondary">Absent</Text>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }}>
                <Text strong style={{ fontSize: '24px', color: '#722ed1' }}>{stats.toBeRescheduled}</Text>
                <br />
                <Text type="secondary">To be rescheduled</Text>
              </Card>
            </Col>
          </Row>

          {/* Timeline List */}
          <Card>
            {timelineItems.length === 0 ? (
              <Alert
                message={t('No classes found')}
                description={t('Try adjusting your filters to see more results')}
                type="info"
                showIcon
              />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={timelineItems}
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} classes`
                }}
                renderItem={(cls) => {
                  const status = cls.attendanceLog ? cls.attendanceLog.status : 'No Attendance';
                  const statusConfig = getStatusConfig(status);
                  const classDate = new Date(cls.date);
                  const today = new Date();
                  const isUpcoming = classDate > today;
                  
                  return (
                    <List.Item
                      style={{
                        padding: '16px',
                        backgroundColor: isUpcoming ? '#f8f9fa' : '#fff',
                        borderLeft: `4px solid ${
                          status === 'Present' ? '#52c41a' :
                          status === 'Late' ? '#faad14' :
                          status === 'Absent' ? '#ff4d4f' :
                          status === 'To be rescheduled' ? '#722ed1' : '#d9d9d9'
                        }`,
                        marginBottom: '8px',
                        borderRadius: '4px'
                      }}
                      actions={[
                        <Button
                          key="edit"
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => handleEditClick(cls)}
                        >
                          {t('Edit')}
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar 
                            size={48}
                            style={{
                              backgroundColor:
                                status === 'Present' ? '#52c41a' :
                                status === 'Late' ? '#faad14' :
                                status === 'Absent' ? '#ff4d4f' :
                                status === 'To be rescheduled' ? '#722ed1' : '#d9d9d9'
                            }}
                            icon={<statusConfig.icon />}
                          />
                        }
                        title={
                          <Space>
                            <Text strong style={{ fontSize: '16px' }}>
                              {cls.class_code} - {cls.subject}
                            </Text>
                            <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                            {isUpcoming && <Tag color="blue">Upcoming</Tag>}
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Space wrap>
                              <Space>
                                <CalendarOutlined style={{ color: '#1890ff' }} />
                                <Text type="secondary">{formatDate(cls.date)}</Text>
                              </Space>
                              <Space>
                                <ClockCircleOutlined style={{ color: '#722ed1' }} />
                                <Text type="secondary">
                                  {formatTime(cls.time)} - {calculateEndTime(cls.time, cls.hours)}
                                </Text>
                                <Tag size="small" color="purple">{cls.hours}</Tag>
                              </Space>
                            </Space>
                            
                            <Space wrap>
                              <Space>
                                <UserOutlined style={{ color: '#52c41a' }} />
                                <Text type="secondary">Teacher: {cls.teacher || 'No Teacher'}</Text>
                              </Space>
                              {cls.classroom && (
                                <Space>
                                  <BookOutlined style={{ color: '#fa8c16' }} />
                                  <Text type="secondary">Room: {cls.classroom}</Text>
                                </Space>
                              )}
                            </Space>

                            {cls.attendanceLog?.homework && (
                              <Tag color={cls.attendanceLog.homework === 'YES' ? 'green' : 'red'}>
                                Homework: {cls.attendanceLog.homework}
                              </Tag>
                            )}
                            
                            {cls.attendanceLog?.comment && (
                              <Alert
                                message={cls.attendanceLog.comment}
                                type={
                                  status === 'Present' ? 'success' :
                                  status === 'Late' ? 'warning' :
                                  status === 'Absent' ? 'error' :
                                  status === 'To be rescheduled' ? 'info' : 'info'
                                }
                                showIcon
                                style={{ marginTop: 8 }}
                              />
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>

          {/* Edit Modal */}
          <Modal
            title={
              <Space>
                <EditOutlined />
                {t('Edit Class')}
              </Space>
            }
            open={editModalVisible}
            onCancel={() => {
              setEditModalVisible(false);
              editForm.resetFields();
            }}
            onOk={handleSaveEdit}
            confirmLoading={saving}
            width={600}
            okText={t('Save')}
            cancelText={t('Cancel')}
          >
            <Form
              form={editForm}
              layout="vertical"
              style={{ marginTop: 24 }}
            >
              <Form.Item
                label={t('Date')}
                name="date"
                rules={[{ required: true, message: t('Please select a date') }]}
              >
                <input
                  type="date"
                  onChange={(e) => editForm.setFieldsValue({ date: e.target.value })}
                  style={{
                    width: '100%',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    padding: '8px 12px',
                    fontSize: 14,
                  }}
                />
              </Form.Item>

              <Form.Item
                label={t('Start Time')}
                name="schedule_time"
                rules={[{ required: true, message: t('Please select start time') }]}
              >
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  options={timeOptions}
                  placeholder={t('Select time')}
                />
              </Form.Item>

              <Form.Item
                label={t('Duration')}
                name="schedule_hour"
                rules={[{ required: true, message: t('Please select duration') }]}
              >
                <Select
                  style={{ width: '100%' }}
                  options={hourOptions}
                  placeholder={t('Select duration')}
                />
              </Form.Item>

              <Form.Item
                label={t('Teacher')}
                name="teacher_id"
              >
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder={t('Select teacher')}
                >
                  {teachers.map((teacher) => (
                    <Select.Option
                      key={teacher.userid}
                      value={teacher.userid}
                      label={`${teacher.nickname} ${teacher.firstname}`}
                    >
                      {teacher.nickname} {teacher.firstname}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={t('Subject')}
                name="subject_id"
              >
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder={t('Select subject')}
                >
                  {subjects.map((subject) => (
                    <Select.Option key={subject.subjectid} value={subject.subjectid}>
                      {subject.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(StudentTimeline, ['admin', 'superadmin']);